'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2CheckBucket } from '@/lib/pr-v2/check-run-presentation';
import type { PrV2CheckRunResponse, PrV2FaviconVisualState } from '@/lib/pr-v2/types';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { buildPrV2ChecksDisplay, classifyPrV2CheckRun } from '@/lib/pr-v2/check-run-presentation';
import { applyPrV2ManagedFavicon, restorePrV2ManagedFavicon } from '@/lib/pr-v2/favicon-dom';
import { PR_V2_FAVICON_VISUAL_STATE } from '@/lib/pr-v2/types';
type PrV2FaviconStateProps = {
  checks: PrV2CheckRunResponse[];
  currentHeadSha: string | null;
  mergeabilityStatus?: MergeabilityStatus | null;
  hasActiveCubicReview?: boolean;
};
const PR_V2_PULL_ROUTE_PATTERN = /^\/pr(?:-v2)?\/[^/]+\/[^/]+\/pull\/[^/]+(?:\/|$)/;
function isPrV2PullRoute(pathname: string): boolean {
  return PR_V2_PULL_ROUTE_PATTERN.test(pathname);
}
function syncPrV2ManagedFavicon({
  pathname,
  faviconState
}: {
  pathname: string;
  faviconState: PrV2FaviconVisualState | null;
}) {
  if (!isPrV2PullRoute(pathname) || !faviconState) {
    restorePrV2ManagedFavicon();
    return;
  }
  applyPrV2ManagedFavicon(faviconState);
}
function getRequiredCheckBuckets({
  normalizedChecks,
  requiredCheckNames
}: {
  normalizedChecks: PrV2CheckRunResponse[];
  requiredCheckNames: ReadonlySet<string>;
}): PrV2CheckBucket[] {
  if (requiredCheckNames.size === 0) {
    return [];
  }
  const bucketByCheckName = new Map<string, PrV2CheckBucket>(normalizedChecks.map(check => [check.name, classifyPrV2CheckRun(check)]));
  return Array.from(requiredCheckNames, checkName => bucketByCheckName.get(checkName) ?? 'running');
}
function getMergeabilityFallbackState(mergeabilityStatus?: MergeabilityStatus | null): PrV2FaviconVisualState | null {
  // GitHub can expose the overall mergeability summary before the required check rows themselves
  // appear in the checks API, so keep this summary-string fallback for that short bootstrap gap only.
  if (mergeabilityStatus?.humanReadableMergeStatus?.startsWith('Required CI failed')) {
    return PR_V2_FAVICON_VISUAL_STATE.Failure;
  }
  if (mergeabilityStatus?.humanReadableMergeStatus?.startsWith('Required CI in progress')) {
    return PR_V2_FAVICON_VISUAL_STATE.Pending;
  }
  return null;
}
function getFaviconVisualState({
  checks,
  currentHeadSha,
  mergeabilityStatus,
  hasActiveCubicReview = false
}: PrV2FaviconStateProps): PrV2FaviconVisualState | null {
  const {
    sortedChecks: normalizedChecks,
    requiredCheckNames
  } = buildPrV2ChecksDisplay({
    checks,
    headSha: currentHeadSha,
    requiredChecks: mergeabilityStatus?.checksStatus.requiredChecks
  });
  const visibleCheckBuckets = normalizedChecks.map(check => classifyPrV2CheckRun(check));
  const requiredCheckBuckets = getRequiredCheckBuckets({
    normalizedChecks,
    requiredCheckNames
  });
  const mergeabilityFallbackState = getMergeabilityFallbackState(mergeabilityStatus);
  if (visibleCheckBuckets.includes('failed') || requiredCheckBuckets.includes('failed')) {
    return PR_V2_FAVICON_VISUAL_STATE.Failure;
  }
  if (mergeabilityFallbackState === PR_V2_FAVICON_VISUAL_STATE.Failure) {
    return PR_V2_FAVICON_VISUAL_STATE.Failure;
  }
  if (hasActiveCubicReview) {
    return PR_V2_FAVICON_VISUAL_STATE.Pending;
  }
  if (requiredCheckBuckets.includes('running') || visibleCheckBuckets.includes('running')) {
    return PR_V2_FAVICON_VISUAL_STATE.Pending;
  }
  if (requiredCheckBuckets.length > 0 && requiredCheckBuckets.every(bucket => bucket === 'passed')) {
    return PR_V2_FAVICON_VISUAL_STATE.Success;
  }
  if (mergeabilityFallbackState) {
    return mergeabilityFallbackState;
  }
  if (normalizedChecks.length > 0 && visibleCheckBuckets.every(bucket => bucket === 'passed')) {
    return PR_V2_FAVICON_VISUAL_STATE.Success;
  }
  return null;
}
export function PrV2FaviconState(t0) {
  const $ = _c(13);
  const {
    checks,
    currentHeadSha,
    mergeabilityStatus,
    hasActiveCubicReview: t1
  } = t0;
  const hasActiveCubicReview = t1 === undefined ? false : t1;
  const pathname = usePathname();
  let t2;
  if ($[0] !== checks || $[1] !== currentHeadSha || $[2] !== hasActiveCubicReview || $[3] !== mergeabilityStatus) {
    t2 = getFaviconVisualState({
      checks,
      currentHeadSha,
      mergeabilityStatus,
      hasActiveCubicReview
    });
    $[0] = checks;
    $[1] = currentHeadSha;
    $[2] = hasActiveCubicReview;
    $[3] = mergeabilityStatus;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const faviconState = t2;
  let t3;
  let t4;
  if ($[5] !== faviconState || $[6] !== pathname) {
    t3 = () => {
      syncPrV2ManagedFavicon({
        pathname,
        faviconState
      });
    };
    t4 = [faviconState, pathname];
    $[5] = faviconState;
    $[6] = pathname;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== faviconState) {
    t5 = () => {
      const syncFromBrowserLocation = () => {
        syncPrV2ManagedFavicon({
          pathname: window.location.pathname,
          faviconState
        });
      };
      window.addEventListener("pageshow", syncFromBrowserLocation);
      return () => {
        window.removeEventListener("pageshow", syncFromBrowserLocation);
      };
    };
    t6 = [faviconState];
    $[9] = faviconState;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useEffect(_temp2, t7);
  return null;
}
function _temp2() {
  return _temp;
}
function _temp() {
  restorePrV2ManagedFavicon();
}